import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction, authSelector } from '../features/auth/slice';
import { User, Lock, LogIn, ShieldCheck, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import PublicLayout from '../components/public/PublicLayout';
import { PUBLIC_BASE } from './public/config';
import LottieAnim from '../components/LottieAnim';

const BG_IMAGES = [
  'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1507838153414-b4b713384a76?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1458560871784-56d23406c091?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1485579149621-3123dd979885?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?auto=format&fit=crop&w=1920&q=70',
  'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1920&q=70',
];

function LoginPage() {
  const bgImage = useMemo(() => BG_IMAGES[Math.floor(Math.random() * BG_IMAGES.length)], []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { me, userType } = useSelector(authSelector.selectAuthState);

  // 이미 member 로그인 상태면 client 탭만 표시
  const memberLoggedIn = me && userType === 'member';
  const defaultTab = memberLoggedIn ? 'client' : (searchParams.get('tab') || 'member');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);  // OTP 입력 단계
  const [otpLoginId, setOtpLoginId] = useState('');  // OTP 대상 loginId
  const [otp, setOtp] = useState('');
  const otpInputRef = useRef(null);

  // 비밀번호 찾기
  const [forgotStep, setForgotStep] = useState(false);
  const [forgotLoginId, setForgotLoginId] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const handleLoginSuccess = (token) => {
    localStorage.setItem('token', token);
    axios.get('/auth/me').then((res) => {
      dispatch(authAction.changeMe(res.data));
      navigate(`${PUBLIC_BASE || '/'}`);
    });
  };

  const doLogin = () => {
    const endpoint =
      activeTab === 'member' ? '/auth/member/login' : '/auth/login';

    setLoginLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}${endpoint}`, { loginId, password })
      .then((response) => {
        if (response.data.token) {
          handleLoginSuccess(response.data.token);
        } else if (response.data.requireOtp) {
          setOtpLoginId(response.data.loginId);
          setOtpStep(true);
          setTimeout(() => otpInputRef.current?.focus(), 100);
        } else {
          alert(response.data.message || '아이디/비밀번호를 확인하세요.');
        }
      })
      .catch((err) => {
        alert(err.response?.data?.message || '로그인에 실패했습니다.');
      })
      .finally(() => {
        setLoginLoading(false);
      });
  };

  const doVerifyOtp = () => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/auth/verify-otp`, { loginId: otpLoginId, otp })
      .then((response) => {
        if (response.data.token) {
          handleLoginSuccess(response.data.token);
        } else {
          alert(response.data.message || '인증 실패');
        }
      })
      .catch((err) => {
        alert(err.response?.data?.message || '인증에 실패했습니다.');
      });
  };

  const doForgotPassword = () => {
    if (!forgotLoginId.trim()) {
      alert('아이디를 입력해 주세요.');
      return;
    }
    if (!forgotEmail || !forgotEmail.includes('@')) {
      alert('올바른 이메일 주소를 입력해 주세요.');
      return;
    }
    setForgotLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/auth/client/forgot-password`, { loginId: forgotLoginId.trim(), email: forgotEmail })
      .then(() => {
        setForgotDone(true);
      })
      .catch((err) => {
        alert(err.response?.data?.message || '처리 중 오류가 발생했습니다.');
      })
      .finally(() => setForgotLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (forgotStep) doForgotPassword();
    else if (otpStep) doVerifyOtp();
    else doLogin();
  };

  const getMe = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token && !me) {
      dispatch(authAction.setLoading(true));
      axios
        .get('/auth/me')
        .then((res) => {
          dispatch(authAction.changeMe(res.data));
        })
        .finally(() => {
          dispatch(authAction.setLoading(false));
        });
    }
  }, [dispatch, me]);

  useEffect(() => {
    getMe();
  }, [getMe]);

  // 이미 로그인 상태면 홈으로 (member→member탭, client 모두)
  useEffect(() => {
    if (!me) return;
    if (userType === 'client' || (userType === 'member' && activeTab === 'member')) {
      navigate(`${PUBLIC_BASE || '/'}`);
    }
  }, [me, userType, activeTab, navigate]);

  // member 로그인 상태에서 진입하면 client 탭으로 강제
  useEffect(() => {
    if (memberLoggedIn && activeTab === 'member') {
      setActiveTab('client');
    }
  }, [memberLoggedIn, activeTab]);

  // 탭 전환 시 입력값 초기화
  useEffect(() => {
    setLoginId('');
    setPassword('');
    setForgotStep(false);
    setForgotLoginId('');
    setForgotEmail('');
    setForgotDone(false);
  }, [activeTab]);

  return (
    <PublicLayout>
      {/* 전체화면 배경 이미지 */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat transition-opacity duration-1000"
        style={{ backgroundImage: `url(${bgImage})` }}>
        <div className="absolute inset-0 bg-black/60" />
      </div>
      <div className="flex justify-center items-center py-16 px-4 min-h-[60vh]">
        <div className="w-full max-w-sm">
          {/* 카드 헤더 */}
          <div className="bg-[#0d1526] rounded-t-2xl px-8 pt-8 pb-6 border border-white/[0.08] border-b-0">
            <h1 className="text-white text-2xl font-bold tracking-tight">로그인</h1>
            <p className="text-slate-400 text-sm mt-1">p!nk에 오신 것을 환영합니다</p>
          </div>

          {/* 탭 (member 로그인 상태면 권리사 탭만 표시) */}
          <div className="flex bg-[#182334] border-x border-white/[0.08]">
            {!memberLoggedIn && (
              <button
                type="button"
                onClick={() => setActiveTab('member')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'member'
                    ? 'text-white border-b-2 border-sky-400'
                    : 'text-slate-500 border-b border-white/[0.08] hover:text-slate-300'
                }`}>
                일반회원
              </button>
            )}
            <button
              type="button"
              onClick={() => setActiveTab('client')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'client'
                  ? 'text-white border-b-2 border-sky-400'
                  : 'text-slate-500 border-b border-white/[0.08] hover:text-slate-300'
              }`}>
              권리사
            </button>
          </div>

          {/* 폼 */}
          <form
            onSubmit={handleSubmit}
            className="bg-[#182334] rounded-b-2xl px-8 py-8 space-y-5 border border-white/[0.08] border-t-0">

            {forgotStep ? (
              /* 비밀번호 찾기 화면 */
              forgotDone ? (
                <>
                  <div className="flex flex-col items-center gap-2 py-2">
                    <CheckCircle size={36} className="text-emerald-400" />
                    <p className="text-white font-semibold text-base">발송 완료</p>
                    <p className="text-slate-400 text-sm text-center">
                      등록된 이메일로 임시 비밀번호를<br />발송했습니다. 메일함을 확인해 주세요.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-xl transition-colors"
                    onClick={() => { setForgotStep(false); setForgotDone(false); }}>
                    <ArrowLeft size={15} />
                    로그인으로 돌아가기
                  </button>
                </>
              ) : (
                <>
                  <div className="flex flex-col items-center gap-2 py-2">
                    <Mail size={36} className="text-sky-400" />
                    <p className="text-white font-semibold text-base">비밀번호 찾기</p>
                    <p className="text-slate-400 text-sm text-center">
                      등록된 이메일을 입력하시면<br />임시 비밀번호를 보내 드립니다.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">아이디</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <User size={15} className="text-slate-500" />
                      </div>
                      <input
                        type="text"
                        autoFocus
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                        placeholder="예) P0001"
                        value={forgotLoginId}
                        onChange={(e) => setForgotLoginId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">이메일</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <Mail size={15} className="text-slate-500" />
                      </div>
                      <input
                        type="email"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                        placeholder="등록된 이메일 주소"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                    <Mail size={16} />
                    {forgotLoading ? '발송 중...' : '임시 비밀번호 받기'}
                  </button>
                  <button
                    type="button"
                    className="w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    onClick={() => setForgotStep(false)}>
                    ← 로그인으로 돌아가기
                  </button>
                </>
              )
            ) : otpStep ? (
              /* OTP 입력 화면 */
              <>
                <div className="flex flex-col items-center gap-2 py-2">
                  <ShieldCheck size={36} className="text-sky-400" />
                  <p className="text-white font-semibold text-base">이메일 인증</p>
                  <p className="text-slate-400 text-sm text-center">
                    등록된 이메일로 발송된<br />6자리 인증코드를 입력하세요.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    인증코드
                  </label>
                  <input
                    ref={otpInputRef}
                    type="text"
                    maxLength={6}
                    inputMode="numeric"
                    className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-lg text-center font-bold text-white tracking-widest placeholder-slate-600 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors duration-200 cursor-pointer">
                  <ShieldCheck size={16} />
                  인증 완료
                </button>
                <button
                  type="button"
                  className="w-full text-center text-xs text-slate-500 hover:text-slate-300 transition-colors"
                  onClick={() => { setOtpStep(false); setOtp(''); }}>
                  ← 로그인으로 돌아가기
                </button>
              </>
            ) : (
              /* 일반 로그인 화면 (forgotStep=false, otpStep=false) */
              <>
                {/* 아이디 */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    아이디
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <User size={15} className="text-slate-500" />
                    </div>
                    <input
                      type="text"
                      name="loginId"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                      placeholder={activeTab === 'client' ? '예) P0001' : '아이디를 입력하세요'}
                      value={loginId}
                      onChange={(e) => setLoginId(e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* 비밀번호 */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    비밀번호
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <Lock size={15} className="text-slate-500" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition"
                      placeholder="비밀번호"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                {/* 로딩 Lottie */}
                {loginLoading && (
                  <div className="flex justify-center">
                    <LottieAnim type="loading" size={60} />
                  </div>
                )}

                {/* 로그인 버튼 */}
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
                  <LogIn size={16} />
                  {loginLoading ? '로그인 중...' : activeTab === 'member' ? '회원 로그인' : '권리사 로그인'}
                </button>

                {/* 일반회원 전용: 회원가입 링크 */}
                {activeTab === 'member' && (
                  <p className="text-center text-sm text-slate-400 mt-2">
                    아직 회원이 아니신가요?{' '}
                    <Link to={`${PUBLIC_BASE}/register`} className="text-sky-400 hover:text-sky-300 hover:underline">
                      회원가입
                    </Link>
                  </p>
                )}

                {/* 권리사 전용: 비밀번호 찾기 링크 */}
                {activeTab === 'client' && (
                  <p className="text-center text-xs text-slate-500 pt-1">
                    최초 비밀번호는 전화번호 뒤 4자리입니다.{' '}
                    <button
                      type="button"
                      className="text-sky-400 hover:text-sky-300 underline underline-offset-2 transition-colors"
                      onClick={() => { setForgotStep(true); setForgotDone(false); setForgotLoginId(''); setForgotEmail(''); }}>
                      비밀번호 찾기
                    </button>
                  </p>
                )}
              </>
            )}
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}

export default LoginPage;
